import { paginatedQuery } from 'lib-shared-api-utils';
import type {
  CreateFlowApiArg,
  CreateFlowApiResponse,
  CreateFlowScheduleApiArg,
  CreateFlowScheduleApiResponse,
  DeleteFlowApiArg,
  DeleteFlowApiResponse,
  DeleteFlowScheduleApiArg,
  DeleteFlowScheduleApiResponse,
  ReadFlowAnnouncementUsageApiArg,
  ReadFlowAnnouncementUsageApiResponse,
  ReadFlowApiArg,
  ReadFlowApiResponse,
  ReadFlowsApiArg,
  ReadFlowsApiResponse,
  ReadFlowScheduleApiArg,
  ReadFlowScheduleApiResponse,
  ReadFlowScheduleBatchApiArg,
  ReadFlowScheduleBatchApiResponse,
  ReadFlowScheduleConflictsApiResponse,
  ReadFlowScheduleCountriesApiResponse,
  ReadFlowScheduleCountryApiArg,
  ReadFlowScheduleCountryApiResponse,
  ReadFlowScheduleFlowsApiArg,
  ReadFlowScheduleFlowsApiResponse,
  ReadFlowScheduleHolidaysApiArg,
  ReadFlowScheduleHolidaysApiResponse,
  ReadFlowSchedulesApiArg,
  ReadFlowSchedulesApiResponse,
  UpdateFlowApiArg,
  UpdateFlowApiResponse,
  UpdateFlowScheduleApiArg,
  UpdateFlowScheduleApiResponse,
  UpdateFlowSchedulePreviewApiArg,
  UpdateFlowSchedulePreviewApiResponse,
} from 'lib-shared-types-api';

import { api } from 'app/app.api';

import { transformFlowCreateOrUpdate } from './utils/transformArg';
import {
  transformFlowResponse,
  transformFlowScheduleCountryResponse,
  transformFlowSchedulePreviewResponse,
  transformFlowScheduleResponse,
  transformFlowsResponse,
} from './utils/transformResponse';

export const flowsApi = api
  .enhanceEndpoints({
    addTagTypes: ['Schedule', 'ScheduleConflicts', 'ScheduleFlows'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      createFlow: builder.mutation<CreateFlowApiResponse, CreateFlowApiArg>({
        query: (flow) => ({
          version: 1,
          path: 'flows',
          method: 'POST',
          body: transformFlowCreateOrUpdate(flow),
        }),
        invalidatesTags: ['Schedule', 'ScheduleConflicts', 'ScheduleFlows'],
      }),
      readFlow: builder.query<ReadFlowApiResponse, ReadFlowApiArg>({
        query: ({ id }) => ({
          version: 1,
          path: `flows/${id}`,
        }),
        transformResponse: transformFlowResponse,
      }),
      updateFlow: builder.mutation<UpdateFlowApiResponse, UpdateFlowApiArg>({
        query: ({ id, flow }) => ({
          version: 1,
          path: `flows/${id}`,
          method: 'PUT',
          body: transformFlowCreateOrUpdate(flow),
        }),
        transformResponse: transformFlowResponse,
        invalidatesTags: ['Schedule', 'ScheduleConflicts', 'ScheduleFlows'],
      }),
      deleteFlow: builder.mutation<DeleteFlowApiResponse, DeleteFlowApiArg>({
        query: ({ id }) => ({
          version: 1,
          path: `flows/${id}`,
          method: 'DELETE',
        }),
        transformResponse: transformFlowResponse,
        invalidatesTags: ['Schedule', 'ScheduleFlows'],
      }),
      readFlows: builder.query<ReadFlowsApiResponse, ReadFlowsApiArg>({
        query: (arg) =>
          paginatedQuery(arg, ({ channel, locations }) => ({
            version: 2,
            path: 'flows',
            params: {
              channel,
              ...(locations?.length ? { locations: locations.join(',') } : {}),
            },
          })),
        transformResponse: transformFlowsResponse,
      }),
      createFlowSchedule: builder.mutation<
        CreateFlowScheduleApiResponse,
        CreateFlowScheduleApiArg
      >({
        query: (schedule) => ({
          version: 2,
          path: 'flows/schedules',
          method: 'POST',
          body: schedule,
        }),
        transformResponse: transformFlowScheduleResponse,
        invalidatesTags: ['ScheduleConflicts'],
      }),
      readFlowSchedule: builder.query<
        ReadFlowScheduleApiResponse,
        ReadFlowScheduleApiArg
      >({
        query: ({ id }) => ({
          version: 2,
          path: `flows/schedules/${id}`,
        }),
        transformResponse: transformFlowScheduleResponse,
        providesTags: ['Schedule'],
      }),
      updateFlowSchedule: builder.mutation<
        UpdateFlowScheduleApiResponse,
        UpdateFlowScheduleApiArg
      >({
        query: ({ id, schedule }) => ({
          version: 2,
          path: `flows/schedules/${id}`,
          method: 'PUT',
          body: schedule,
        }),
        transformResponse: transformFlowScheduleResponse,
        invalidatesTags: (_response, _error, { id }) => [
          'ScheduleConflicts',
          { type: 'Schedule', id },
        ],
      }),
      deleteFlowSchedule: builder.mutation<
        DeleteFlowScheduleApiResponse,
        DeleteFlowScheduleApiArg
      >({
        query: ({ id }) => ({
          version: 2,
          path: `flows/schedules/${id}`,
          method: 'DELETE',
        }),
        transformResponse: transformFlowScheduleResponse,
        invalidatesTags: (_response, _error, { id }) => [
          'ScheduleConflicts',
          { type: 'Schedule', id },
        ],
      }),
      readFlowsSchedules: builder.query<
        ReadFlowSchedulesApiResponse,
        ReadFlowSchedulesApiArg
      >({
        query: (arg) =>
          paginatedQuery(arg, () => ({
            version: 2,
            path: 'flows/schedules',
          })),
      }),
      readFlowsSchedulesConflicts: builder.query<
        ReadFlowScheduleConflictsApiResponse,
        void
      >({
        query: () => ({
          version: 2,
          path: 'flows/schedules/conflicts',
        }),
        providesTags: [{ type: 'ScheduleConflicts', id: 'LIST' }],
      }),
      readFlowsSchedulesFlows: builder.query<
        ReadFlowScheduleFlowsApiResponse,
        ReadFlowScheduleFlowsApiArg
      >({
        query: (arg) =>
          paginatedQuery(arg, ({ id }) => ({
            version: 2,
            path: `flows/schedules/${id}/flows`,
          })),
        providesTags: ['ScheduleFlows'],
      }),
      readFlowsSchedulesBatch: builder.query<
        ReadFlowScheduleBatchApiResponse,
        ReadFlowScheduleBatchApiArg
      >({
        query: ({ ids }) => ({
          version: 2,
          path: `flows/schedules/batch`,
          params: { ids: ids.join(',') },
        }),
      }),
      readFlowsSchedulesCountry: builder.query<
        ReadFlowScheduleCountryApiResponse,
        ReadFlowScheduleCountryApiArg
      >({
        query: ({ country_code }) => ({
          version: 2,
          path: `flows/schedules/countries/${country_code}`,
        }),
        transformResponse: transformFlowScheduleCountryResponse,
      }),
      readFlowsSchedulesCountries: builder.query<
        ReadFlowScheduleCountriesApiResponse,
        void
      >({
        query: () => ({
          version: 2,
          path: 'flows/schedules/countries',
        }),
      }),
      readFlowsSchedulesHolidays: builder.query<
        ReadFlowScheduleHolidaysApiResponse,
        ReadFlowScheduleHolidaysApiArg
      >({
        query: (arg) =>
          paginatedQuery(arg, ({ country_code, timezone }) => ({
            version: 2,
            path: `flows/schedules/holidays/${country_code}`,
            params: { timezone },
          })),
      }),
      readFlowsSchedulesPreview: builder.mutation<
        UpdateFlowSchedulePreviewApiResponse,
        UpdateFlowSchedulePreviewApiArg
      >({
        query: ({ id, payload }) => ({
          version: 2,
          path: `flows/schedules/${id}/preview`,
          method: 'PUT',
          body: payload,
        }),
        transformResponse: transformFlowSchedulePreviewResponse,
      }),
      readFlowsAnnouncementUsage: builder.query<
        ReadFlowAnnouncementUsageApiResponse,
        ReadFlowAnnouncementUsageApiArg
      >({
        query: ({ id }) => ({
          version: 2,
          path: `flows/announcements/${id}/flows`,
        }),
      }),
    }),
  });
